import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Steps, Upload, Button, message, Descriptions, Table, Tag, Radio, Spin, Alert, Checkbox, Row, Col, Statistic } from 'antd';
import { UploadOutlined, ScissorOutlined, ThunderboltOutlined, CloudUploadOutlined, CheckCircleOutlined, ExperimentOutlined, RobotOutlined, BarChartOutlined, InboxOutlined } from '@ant-design/icons';
import { FilePdfOutlined, FileWordOutlined, FilePptOutlined, FileExcelOutlined, FileMarkdownOutlined, FileTextOutlined, PlusOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import * as API from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { useThemeStyles } from '../context/ThemeContext';

const DeployProject = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const themeStyles = useThemeStyles();
  const [currentStep, setCurrentStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadedDoc, setUploadedDoc] = useState(null);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [selectedStrategies, setSelectedStrategies] = useState([]);
  const [comparing, setComparing] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [chunking, setChunking] = useState(false);
  const [chunkData, setChunkData] = useState(null);
  const [embedding, setEmbedding] = useState(false);
  const [embeddingData, setEmbeddingData] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [pushData, setPushData] = useState(null);
  const [vectorStoreInfo, setVectorStoreInfo] = useState(null);

  // Helper function to estimate tokens (rough estimate: 4 chars ≈ 1 token)
  const estimateTokens = (text) => {
    return Math.ceil(text.length / 4);
  };

  // Helper function to calculate chunk analytics
  const calculateChunkAnalytics = (chunkData) => {
    if (!chunkData || !chunkData.chunks || chunkData.chunks.length === 0) {
      return null;
    }

    const chunks = chunkData.chunks;
    const chunkSizes = chunks.map(chunk => chunk.content.length);
    const totalChars = chunkSizes.reduce((sum, size) => sum + size, 0);
    const avgChunkSize = Math.round(totalChars / chunks.length);
    const minChunkSize = Math.min(...chunkSizes);
    const maxChunkSize = Math.max(...chunkSizes);
    
    // Estimate tokens for each chunk
    const totalTokens = chunks.reduce((sum, chunk) => sum + estimateTokens(chunk.content), 0);
    const avgTokensPerChunk = Math.round(totalTokens / chunks.length);
    
    // Estimate embedding cost (using text-embedding-ada-002 pricing: $0.0001 per 1K tokens)
    // For text-embedding-3-small: $0.00002 per 1K tokens
    // Using conservative estimate with ada-002 pricing
    const embeddingCostPer1K = 0.0001; // USD
    const estimatedCost = (totalTokens / 1000) * embeddingCostPer1K;

    return {
      totalChunks: chunks.length,
      totalChars,
      avgChunkSize,
      minChunkSize,
      maxChunkSize,
      totalTokens,
      avgTokensPerChunk,
      estimatedCost,
    };
  };

  const steps = [
    { title: 'Upload Document' },
    { title: 'Chunking Strategy' },
    { title: 'Generate Chunks' },
    { title: 'Generate Embeddings' },
    { title: 'Push to Vector Store' },
    { title: 'Review' },
  ];

  const stepItems = steps.map((step, index) => ({
    title: step.title,
  }));

  // Fetch AI recommendations when reaching step 1
  useEffect(() => {
    if (currentStep === 1 && uploadedDoc && !recommendations && !loadingRecommendations) {
      fetchRecommendations();
    }
  }, [currentStep, uploadedDoc]);

  const fetchRecommendations = async () => {
    if (!uploadedDoc) return;
    
    setLoadingRecommendations(true);
    try {
      const token = localStorage.getItem('token');
      const response = await API.getAIRecommendations(token, projectId, uploadedDoc.doc_id);
      if (response && response.data) {
        setRecommendations(response.data);
        // Auto-select the 2 recommended strategies
        setSelectedStrategies(response.data.recommended || []);
      }
    } catch (error) {
      console.error('Error fetching AI recommendations:', error);
      message.warning('Could not fetch AI recommendations. You can still select strategies manually.');
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const handleStrategySelection = (strategy, checked) => {
    if (checked) {
      // Add strategy if less than 3 selected
      if (selectedStrategies.length < 3) {
        setSelectedStrategies([...selectedStrategies, strategy]);
      } else {
        message.warning('You can select a maximum of 3 strategies to compare.');
      }
    } else {
      // Remove strategy
      setSelectedStrategies(selectedStrategies.filter(s => s !== strategy));
    }
  };

  const handleCompareStrategies = async () => {
    if (!uploadedDoc || selectedStrategies.length === 0) {
      message.warning('Please select at least one strategy.');
      return;
    }
    
    if (selectedStrategies.length === 1) {
      // If only 1 strategy selected, use it directly without comparison
      setSelectedStrategy(selectedStrategies[0]);
      message.success(`Strategy "${selectedStrategies[0]}" selected. You can proceed to chunking.`);
      return;
    }
    
    if (selectedStrategies.length > 3) {
      message.warning('Please select a maximum of 3 strategies.');
      return;
    }
    
    // Only compare if 2-3 strategies are selected
    setComparing(true);
    try {
      const token = localStorage.getItem('token');
      console.log('Comparing strategies for doc:', uploadedDoc.doc_id, 'strategies:', selectedStrategies);
      const response = await API.compareChunkingStrategies(token, projectId, uploadedDoc.doc_id, selectedStrategies);
      console.log('Comparison response:', response);
      if (response && response.data) {
        setComparisonData(response.data);
        if (response.data.winner) {
          setSelectedStrategy(response.data.winner);
          message.success('Strategy comparison completed!');
        }
      } else {
        console.error('Invalid response structure:', response);
        message.warning('Comparison completed but no data received.');
      }
    } catch (error) {
      console.error('Error comparing strategies:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      message.error('Failed to compare strategies. Please try again.');
    } finally {
      setComparing(false);
    }
  };

  const getFileIcon = (filename) => {
    if (!filename) return <FileTextOutlined style={{ fontSize: '24px', color: '#1890ff' }} />;
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return <FilePdfOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />;
      case 'doc':
      case 'docx':
        return <FileWordOutlined style={{ fontSize: '24px', color: '#2b579a' }} />;
      case 'ppt':
      case 'pptx':
        return <FilePptOutlined style={{ fontSize: '24px', color: '#ff9800' }} />;
      case 'xls':
      case 'xlsx':
        return <FileExcelOutlined style={{ fontSize: '24px', color: '#52c41a' }} />;
      case 'md':
        return <FileMarkdownOutlined style={{ fontSize: '24px', color: '#722ed1' }} />;
      default:
        return <FileTextOutlined style={{ fontSize: '24px', color: '#1890ff' }} />;
    }
  };

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await API.uploadDocument(token, projectId, formData);
      setUploadedDoc(response.data);
      message.success('Document uploaded successfully!');
      setCurrentStep(1);
    } catch (error) {
      console.error('Error uploading document:', error);
      message.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
    return false;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <>
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: themeStyles.textPrimary, marginBottom: '8px' }}>
                Upload Document
              </h3>
              <p style={{ margin: 0, color: themeStyles.textSecondary, fontSize: '14px' }}>Upload your document to begin the deployment process.</p>
            </div>
            <Upload.Dragger
              beforeUpload={handleUpload}
              showUploadList={false}
              accept=".pdf,.doc,.docx,.txt,.md,.ppt,.pptx,.xls,.xlsx,.json,.yaml,.yml"
              style={{
                background: 'transparent',
                border: '2px dashed rgba(148, 22, 127, 0.3)',
                borderRadius: '8px',
                padding: '60px 20px',
                marginBottom: '24px'
              }}
            >
              <p className="ant-upload-drag-icon" style={{ marginBottom: '16px' }}>
                <InboxOutlined style={{ fontSize: '64px', color: '#94167F' }} />
              </p>
              <p className="ant-upload-text" style={{ color: themeStyles.textPrimary, fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                Click or drag file to this area to upload
              </p>
              <p className="ant-upload-hint" style={{ color: themeStyles.textSecondary, fontSize: '14px', marginBottom: '24px' }}>
                Supported formats include PDF, DOCX, TXT and more
              </p>
              <Button 
                icon={<CloudUploadOutlined />} 
                size="large"
                loading={uploading}
                style={{
                  background: 'linear-gradient(135deg, #94167F 0%, #153CB4 100%)',
                  border: 'none',
                  color: '#fff'
                }}
              >
                  Upload Document
                </Button>
            </Upload.Dragger>
            
            {uploadedDoc && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '500', color: themeStyles.textPrimary }}>
                  Uploaded Document
                </h4>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  background: 'rgba(148, 22, 127, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(148, 22, 127, 0.3)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    {getFileIcon(uploadedDoc.filename)}
                    <div>
                      <div style={{ color: themeStyles.textPrimary, fontSize: '14px', fontWeight: '500' }}>
                        {uploadedDoc.filename}
            </div>
                      <div style={{ color: themeStyles.textSecondary, fontSize: '12px', marginTop: '4px' }}>
                        {(uploadedDoc.file_size / (1024 * 1024)).toFixed(2)} MB
                </div>
                </div>
                </div>
                  <Button
                    icon={<DeleteOutlined />}
                    type="text"
                    danger
                    onClick={() => {
                      setUploadedDoc(null);
                      setCurrentStep(0);
                    }}
                    style={{ color: '#ff4d4f' }}
                  />
                </div>
                </div>
            )}
          </>
        );
      case 1:
        return (
          <>
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: themeStyles.textPrimary, marginBottom: '8px' }}>
                Chunking Strategy
              </h3>
              <p style={{ margin: 0, color: themeStyles.textSecondary, fontSize: '14px' }}>
                Select 1 strategy to use directly, or 2-3 strategies to compare. AI-recommended strategies are shown at the top.
              </p>
            </div>
            <div style={{ marginBottom: '24px', padding: '12px', background: 'rgba(148, 22, 127, 0.05)', borderRadius: '4px', border: '1px solid rgba(148, 22, 127, 0.2)' }}>
              {selectedStrategies.length > 0 && (
                <p style={{ margin: '8px 0 0 0', color: themeStyles.textPrimary, fontSize: '14px', fontWeight: '500' }}>
                  Selected: {selectedStrategies.length}/3 strategies
                  {selectedStrategies.length === 1 && ' (will proceed directly to chunking)'}
                  {selectedStrategies.length >= 2 && selectedStrategies.length <= 3 && ' (will compare strategies)'}
                </p>
              )}
            </div>
            
            {loadingRecommendations && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
                <div style={{ marginTop: '24px', color: themeStyles.textSecondary }}>
                  Getting AI recommendations...
                </div>
              </div>
            )}

            {!loadingRecommendations && recommendations && (
              <>
                {/* AI Recommended Strategies */}
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ color: themeStyles.textPrimary, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <RobotOutlined style={{ color: '#52c41a' }} />
                    AI Recommended Strategies
                  </h3>
                  {recommendations.recommended?.map((strategy) => (
                    <div key={strategy} style={{ marginBottom: '12px' }}>
                      <Checkbox
                        checked={selectedStrategies.includes(strategy)}
                        onChange={(e) => handleStrategySelection(strategy, e.target.checked)}
                        style={{ width: '100%' }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '12px', background: selectedStrategies.includes(strategy) ? 'rgba(82, 196, 26, 0.15)' : 'rgba(82, 196, 26, 0.05)', borderRadius: '4px', border: `1px solid ${selectedStrategies.includes(strategy) ? 'rgba(82, 196, 26, 0.5)' : 'rgba(82, 196, 26, 0.2)'}`, marginLeft: '8px' }}>
                          <div>
                            <div style={{ fontWeight: 'bold', color: themeStyles.textPrimary, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {strategy.charAt(0).toUpperCase() + strategy.slice(1)}
                              <Tag color="success">AI Recommended</Tag>
                            </div>
                            <div style={{ fontSize: '12px', color: themeStyles.textSecondary }}>
                              {recommendations.strategy_descriptions?.[strategy] || 'No description available'}
                            </div>
                          </div>
                        </div>
                      </Checkbox>
                    </div>
                  ))}
                </div>

                {/* Other Strategies */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: themeStyles.textPrimary, marginBottom: '16px' }}>
                    Other Strategies
                  </h3>
                  {recommendations.remaining?.map((strategy) => (
                    <div key={strategy} style={{ marginBottom: '12px' }}>
                      <Checkbox
                        checked={selectedStrategies.includes(strategy)}
                        onChange={(e) => handleStrategySelection(strategy, e.target.checked)}
                        disabled={!selectedStrategies.includes(strategy) && selectedStrategies.length >= 3}
                        style={{ width: '100%' }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '12px', background: selectedStrategies.includes(strategy) ? 'rgba(148, 22, 127, 0.15)' : 'rgba(148, 22, 127, 0.05)', borderRadius: '4px', border: `1px solid ${selectedStrategies.includes(strategy) ? 'rgba(148, 22, 127, 0.5)' : 'rgba(148, 22, 127, 0.2)'}`, marginLeft: '8px', opacity: !selectedStrategies.includes(strategy) && selectedStrategies.length >= 3 ? 0.5 : 1 }}>
                          <div>
                            <div style={{ fontWeight: 'bold', color: themeStyles.textPrimary, marginBottom: '4px' }}>
                              {strategy.charAt(0).toUpperCase() + strategy.slice(1)}
                            </div>
                            <div style={{ fontSize: '12px', color: themeStyles.textSecondary }}>
                              {recommendations.strategy_descriptions?.[strategy] || 'No description available'}
                            </div>
                          </div>
                        </div>
                      </Checkbox>
                    </div>
                  ))}
                </div>

                {/* Compare/Proceed Button */}
                <div style={{ textAlign: 'center', marginTop: '32px' }}>
                  <Button 
                    icon={selectedStrategies.length === 1 ? <CheckCircleOutlined /> : <ExperimentOutlined />}
                    size="large"
                    type="primary"
                    loading={comparing}
                    disabled={selectedStrategies.length === 0 || selectedStrategies.length > 3}
                    onClick={handleCompareStrategies}
                  >
                    {selectedStrategies.length === 1 
                      ? `Use Strategy: ${selectedStrategies[0].charAt(0).toUpperCase() + selectedStrategies[0].slice(1)}`
                      : `Compare Selected Strategies (${selectedStrategies.length})`
                    }
                  </Button>
                  {selectedStrategies.length === 0 && (
                    <div style={{ marginTop: '12px', color: themeStyles.textSecondary, fontSize: '12px' }}>
                      Please select at least one strategy
                    </div>
                  )}
                  {selectedStrategies.length === 1 && (
                    <div style={{ marginTop: '12px', color: themeStyles.textSecondary, fontSize: '12px' }}>
                      This strategy will be used directly for chunking
                    </div>
                  )}
                  {selectedStrategies.length >= 2 && selectedStrategies.length <= 3 && (
                    <div style={{ marginTop: '12px', color: themeStyles.textSecondary, fontSize: '12px' }}>
                      These strategies will be compared to find the best one
                    </div>
                  )}
                </div>
              </>
            )}

            {!loadingRecommendations && !recommendations && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Button 
                  icon={<RobotOutlined />}
                  size="large"
                  onClick={fetchRecommendations}
                >
                  Get AI Recommendations
                </Button>
              </div>
            )}
            
            {comparing && !comparisonData && (
              <div style={{ textAlign: 'center', padding: '60px', marginTop: '24px' }}>
                <Spin size="large" />
                <div style={{ marginTop: '24px', color: themeStyles.textSecondary }}>
                  Analyzing chunking strategies... This may take a few minutes.
                </div>
              </div>
            )}

            {!comparing && comparisonData && (
              <>
                {comparisonData.error ? (
                  <Alert
                    message="Comparison Error"
                    description={comparisonData.error}
                    type="error"
                    showIcon
                    style={{ marginBottom: '24px' }}
                  />
                ) : (
                  <>
                    <Alert
                      message={`Best Strategy: ${comparisonData.winner}`}
                      description={`Score: ${comparisonData.winner_score?.toFixed(3)} - ${comparisonData.reasoning || ''}`}
                      type="success"
                      showIcon
                      style={{ marginBottom: '24px' }}
                    />
                    
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{ color: themeStyles.textPrimary, marginBottom: '16px' }}>Select Final Chunking Strategy:</h3>
                      <Radio.Group
                        value={selectedStrategy}
                        onChange={(e) => setSelectedStrategy(e.target.value)}
                        style={{ width: '100%' }}
                      >
                        {comparisonData.comparison_table?.map((item) => (
                          <div key={item.strategy} style={{ marginBottom: '16px' }}>
                            <Radio value={item.strategy} style={{ width: '100%' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '12px', background: selectedStrategy === item.strategy ? 'rgba(148, 22, 127, 0.15)' : 'rgba(148, 22, 127, 0.05)', borderRadius: '4px', border: `1px solid ${selectedStrategy === item.strategy ? 'rgba(148, 22, 127, 0.5)' : 'rgba(148, 22, 127, 0.2)'}` }}>
                                <div>
                                  <div style={{ fontWeight: 'bold', color: themeStyles.textPrimary, marginBottom: '4px' }}>
                                    {item.strategy.charAt(0).toUpperCase() + item.strategy.slice(1)}
                                    {item.strategy === comparisonData.winner && (
                                      <Tag color="success" style={{ marginLeft: '8px' }}>Best Score</Tag>
                                    )}
                                  </div>
                                  <div style={{ fontSize: '12px', color: themeStyles.textSecondary }}>
                                    {recommendations?.strategy_descriptions?.[item.strategy] || 
                                     (item.strategy === 'semantic' && 'Groups text by semantic similarity using AI embeddings') ||
                                     (item.strategy === 'markdown' && 'Preserves markdown structure (headers, lists, code blocks)') ||
                                     (item.strategy === 'recursive' && 'Splits text recursively by different separators (paragraphs, sentences, words)') ||
                                     (item.strategy === 'character' && 'Simple character-based splitting with fixed size') ||
                                     (item.strategy === 'hybrid' && 'Two-pass chunking: markdown/recursive + semantic refinement (best quality)')}
                                  </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: themeStyles.textPrimary }}>
                                    {item.final_score?.toFixed(3)}
                                  </div>
                                  <div style={{ fontSize: '11px', color: themeStyles.textSecondary }}>
                                    Final Score
                                  </div>
                                </div>
                              </div>
                            </Radio>
                          </div>
                        ))}
                      </Radio.Group>
                    </div>

                    <div style={{ marginTop: '24px' }}>
                      <h4 style={{ color: themeStyles.textPrimary, marginBottom: '12px' }}>Detailed Metrics:</h4>
                      <Table
                        tableLayout="fixed"
                        scroll={{ x: 'max-content' }}
                        columns={[
                          {
                            title: 'Strategy',
                            dataIndex: 'strategy',
                            key: 'strategy',
                            render: (text, record) => (
                              <div>
                                <strong>{text}</strong>
                                {text === comparisonData.winner && (
                                  <Tag color="success" style={{ marginLeft: '8px' }}>Best</Tag>
                                )}
                              </div>
                            ),
                          },
                          {
                            title: 'Final Score',
                            dataIndex: 'final_score',
                            key: 'final_score',
                            sorter: (a, b) => a.final_score - b.final_score,
                            defaultSortOrder: 'descend',
                            render: (score) => score?.toFixed(3),
                          },
                          {
                            title: 'Intrinsic',
                            dataIndex: 'intrinsic',
                            key: 'intrinsic',
                            render: (score) => score?.toFixed(3),
                          },
                          {
                            title: 'Extrinsic',
                            dataIndex: 'extrinsic',
                            key: 'extrinsic',
                            render: (score) => score?.toFixed(3),
                          },
                          {
                            title: 'Chunks',
                            dataIndex: 'chunks',
                            key: 'chunks',
                          },
                          {
                            title: 'QA Score',
                            dataIndex: 'qa_score',
                            key: 'qa_score',
                            render: (score) => score?.toFixed(3),
                          },
                        ]}
                        dataSource={comparisonData.comparison_table?.map(item => ({
                          key: item.strategy,
                          ...item,
                        })) || []}
                        pagination={false}
                        size="small"
                      />
                    </div>
                  </>
                )}
              </>
            )}

          </>
        );
      case 2:
        return (
          <>
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: themeStyles.textPrimary, marginBottom: '8px' }}>
                Generate Chunks
              </h3>
              <p style={{ margin: 0, color: themeStyles.textSecondary, fontSize: '14px' }}>Split your document into smaller chunks for processing. This step breaks down the content into manageable pieces using the selected strategy.</p>
            </div>
            <div style={{ marginBottom: '24px', padding: '12px', background: 'rgba(148, 22, 127, 0.05)', borderRadius: '4px', border: '1px solid rgba(148, 22, 127, 0.2)' }}>
              {selectedStrategy && (
                <p style={{ margin: '8px 0 0 0', color: themeStyles.textPrimary, fontSize: '14px', fontWeight: '500' }}>
                  Selected Strategy: <Tag color="blue">{selectedStrategy}</Tag>
                </p>
              )}
            </div>
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Button 
                icon={<ScissorOutlined />}
                size="large" 
                loading={chunking}
                disabled={chunkData || !selectedStrategy}
                onClick={async () => {
                  setChunking(true);
                  try {
                    const token = localStorage.getItem('token');
                    const response = await API.generateChunks(token, projectId, uploadedDoc.doc_id, selectedStrategy);
                    setChunkData(response.data);
                    message.success('Chunks generated successfully!');
                  } catch (error) {
                    console.error('Error generating chunks:', error);
                    message.error('Failed to generate chunks');
                  } finally {
                    setChunking(false);
                  }
                }}
              >
                Generate Chunks
              </Button>
              {chunkData && (() => {
                const analytics = calculateChunkAnalytics(chunkData);
                return (
                  <div style={{ marginTop: '24px' }}>
                    {/* Analytics Section */}
                    {analytics && (
                      <div style={{ marginBottom: '32px', padding: '20px', background: 'rgba(148, 22, 127, 0.08)', borderRadius: '8px', border: '1px solid rgba(148, 22, 127, 0.3)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                          <BarChartOutlined style={{ fontSize: '18px', color: '#94167F' }} />
                          <h3 style={{ margin: 0, color: themeStyles.textPrimary, fontSize: '18px', fontWeight: '600' }}>
                            Chunk Analytics
                          </h3>
                        </div>
                        <Row gutter={[16, 16]}>
                          <Col xs={24} sm={12} md={8} lg={6}>
                            <Card size="small" style={{ background: 'rgba(148, 22, 127, 0.1)', border: '1px solid rgba(148, 22, 127, 0.3)' }}>
                              <Statistic
                                title="Total Chunks"
                                value={analytics.totalChunks}
                                valueStyle={{ color: themeStyles.textPrimary }}
                              />
                            </Card>
                          </Col>
                          <Col xs={24} sm={12} md={8} lg={6}>
                            <Card size="small" style={{ background: 'rgba(148, 22, 127, 0.1)', border: '1px solid rgba(148, 22, 127, 0.3)' }}>
                              <Statistic
                                title="Avg Chunk Size"
                                value={analytics.avgChunkSize}
                                suffix="chars"
                                valueStyle={{ color: themeStyles.textPrimary }}
                              />
                            </Card>
                          </Col>
                          <Col xs={24} sm={12} md={8} lg={6}>
                            <Card size="small" style={{ background: 'rgba(148, 22, 127, 0.1)', border: '1px solid rgba(148, 22, 127, 0.3)' }}>
                              <Statistic
                                title="Total Tokens"
                                value={analytics.totalTokens.toLocaleString()}
                                valueStyle={{ color: themeStyles.textPrimary }}
                              />
                            </Card>
                          </Col>
                          <Col xs={24} sm={12} md={8} lg={6}>
                            <Card size="small" style={{ background: 'rgba(148, 22, 127, 0.1)', border: '1px solid rgba(148, 22, 127, 0.3)' }}>
                              <Statistic
                                title="Est. Embedding Cost"
                                value={analytics.estimatedCost.toFixed(4)}
                                prefix="$"
                                valueStyle={{ color: '#52c41a' }}
                              />
                            </Card>
                          </Col>
                          <Col xs={24} sm={12} md={8} lg={6}>
                            <Card size="small" style={{ background: 'rgba(148, 22, 127, 0.1)', border: '1px solid rgba(148, 22, 127, 0.3)' }}>
                              <Statistic
                                title="Min Chunk Size"
                                value={analytics.minChunkSize}
                                suffix="chars"
                                valueStyle={{ color: themeStyles.textPrimary }}
                              />
                            </Card>
                          </Col>
                          <Col xs={24} sm={12} md={8} lg={6}>
                            <Card size="small" style={{ background: 'rgba(148, 22, 127, 0.1)', border: '1px solid rgba(148, 22, 127, 0.3)' }}>
                              <Statistic
                                title="Max Chunk Size"
                                value={analytics.maxChunkSize}
                                suffix="chars"
                                valueStyle={{ color: themeStyles.textPrimary }}
                              />
                            </Card>
                          </Col>
                          <Col xs={24} sm={12} md={8} lg={6}>
                            <Card size="small" style={{ background: 'rgba(148, 22, 127, 0.1)', border: '1px solid rgba(148, 22, 127, 0.3)' }}>
                              <Statistic
                                title="Avg Tokens/Chunk"
                                value={analytics.avgTokensPerChunk}
                                valueStyle={{ color: themeStyles.textPrimary }}
                              />
                            </Card>
                          </Col>
                          <Col xs={24} sm={12} md={8} lg={6}>
                            <Card size="small" style={{ background: 'rgba(148, 22, 127, 0.1)', border: '1px solid rgba(148, 22, 127, 0.3)' }}>
                              <Statistic
                                title="Total Characters"
                                value={analytics.totalChars.toLocaleString()}
                                valueStyle={{ color: themeStyles.textPrimary }}
                              />
                            </Card>
                          </Col>
                        </Row>
                        <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(82, 196, 26, 0.1)', borderRadius: '4px', border: '1px solid rgba(82, 196, 26, 0.3)' }}>
                          <p style={{ margin: 0, fontSize: '12px', color: themeStyles.textSecondary }}>
                            <InfoCircleOutlined style={{ marginRight: '6px' }} />
                            Cost estimate based on text-embedding-3-small pricing ($0.00002 per 1K tokens). 
                            Actual cost may vary based on your embedding model configuration.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </>
        );
      case 3:
        return (
          <>
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: themeStyles.textPrimary, marginBottom: '8px' }}>
                Generate Embeddings
              </h3>
              <p style={{ margin: 0, color: themeStyles.textSecondary, fontSize: '14px' }}>Convert text chunks into vector embeddings using your configured embedding model. This enables semantic search capabilities.</p>
            </div>
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Button 
                icon={<ThunderboltOutlined />}
                size="large" 
                loading={embedding}
                disabled={embeddingData}
                onClick={async () => {
                  setEmbedding(true);
                  try {
                    const token = localStorage.getItem('token');
                    const response = await API.generateEmbeddings(token, projectId, uploadedDoc.doc_id);
                    setEmbeddingData(response.data);
                    message.success('Embeddings generated successfully!');
                  } catch (error) {
                    console.error('Error generating embeddings:', error);
                    message.error('Failed to generate embeddings');
                  } finally {
                    setEmbedding(false);
                  }
                }}
              >
                Generate Embeddings
              </Button>
              {embeddingData && (
                <div style={{ marginTop: '24px', textAlign: 'center' }}>
                  <div style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)' }}>
                    Embedded {embeddingData.embedded_count} out of {embeddingData.total_chunks} chunks
                  </div>
                </div>
              )}
            </div>
          </>
        );
      case 4:
        return (
          <>
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: themeStyles.textPrimary, marginBottom: '8px' }}>
                Push to Vector Store
              </h3>
              <p style={{ margin: 0, color: themeStyles.textSecondary, fontSize: '14px' }}>Store the generated embeddings in your vector database. This makes your content searchable and ready for retrieval.</p>
            </div>
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Button 
                icon={<CloudUploadOutlined />}
                size="large" 
                loading={pushing}
                disabled={pushData}
                onClick={async () => {
                  setPushing(true);
                  try {
                    const token = localStorage.getItem('token');
                    const response = await API.pushVectors(token, projectId, uploadedDoc?.doc_id);
                    setPushData(response.data);
                    // Fetch project info to get vector store details
                    try {
                      const projectResponse = await API.getProject(projectId, { token: localStorage.getItem("token") });
                      if (projectResponse.data.config?.vector_store) {
                        setVectorStoreInfo({
                          type: projectResponse.data.config.vector_store.type || 'unknown',
                          collection_name: projectResponse.data.config.vector_store.collection_name || 'unknown',
                        });
                      }
                    } catch (error) {
                      console.error('Error fetching vector store info:', error);
                    }
                    message.success('Vectors pushed successfully!');
                  } catch (error) {
                    console.error('Error pushing vectors:', error);
                    message.error('Failed to push vectors');
                  } finally {
                    setPushing(false);
                  }
                }}
              >
                Push to Vector Store
              </Button>
              {pushData && (
                <div style={{ marginTop: '24px', textAlign: 'center' }}>
                  <div style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.85)', marginBottom: '16px' }}>
                    Pushed {pushData.pushed_count} out of {pushData.total_chunks} chunks
                  </div>
                  {vectorStoreInfo && (
                    <div style={{ 
                      marginTop: '16px', 
                      padding: '16px', 
                      background: 'rgba(148, 22, 127, 0.1)', 
                      borderRadius: '8px', 
                      border: '1px solid rgba(148, 22, 127, 0.3)',
                      textAlign: 'left',
                      display: 'inline-block'
                    }}>
                      <div style={{ fontSize: '14px', color: themeStyles.textPrimary, fontWeight: '500', marginBottom: '8px' }}>
                        Vector Database Information:
                      </div>
                      <div style={{ fontSize: '13px', color: themeStyles.textSecondary, marginBottom: '4px' }}>
                        <strong>Database Type:</strong> {vectorStoreInfo.type}
                      </div>
                      <div style={{ fontSize: '13px', color: themeStyles.textSecondary }}>
                        <strong>Collection Name:</strong> {vectorStoreInfo.collection_name}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        );
      case 5:
        return (
          <>
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: themeStyles.textPrimary, marginBottom: '8px' }}>
                Review
              </h3>
              <p style={{ margin: 0, color: themeStyles.textSecondary, fontSize: '14px' }}>Your project is now ready to use! Please review the deployment summary.</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <CheckCircleOutlined style={{ fontSize: '80px', color: '#52c41a', marginBottom: '16px' }} />
                <div style={{ fontSize: '28px', fontWeight: '600', color: themeStyles.textPrimary }}>
                  Project Successfully Deployed!
                </div>
              </div>
              <div style={{ width: '100%', marginBottom: '32px' }}>
                <Descriptions column={1} bordered style={{ width: '100%' }}>
                  <Descriptions.Item label="Document">{uploadedDoc?.filename || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="File Size">{uploadedDoc?.file_size ? `${(uploadedDoc.file_size / 1024).toFixed(2)} KB` : 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Page Count">{uploadedDoc?.page_count || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Chunks Generated">{chunkData?.chunk_count || chunkData?.chunks?.length || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Embeddings Created">{embeddingData ? `${embeddingData.embedded_count || 0} / ${embeddingData.total_chunks || 0}` : 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Vectors Pushed">{pushData ? `${pushData.pushed_count || 0} / ${pushData.total_chunks || 0}` : 'N/A'}</Descriptions.Item>
                  {vectorStoreInfo && (
                    <>
                      <Descriptions.Item label="Vector Database Type">{vectorStoreInfo.type}</Descriptions.Item>
                      <Descriptions.Item label="Collection Name">{vectorStoreInfo.collection_name}</Descriptions.Item>
                    </>
                  )}
              </Descriptions>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-start', flexWrap: 'wrap', width: '100%' }}>
                <Button size="large" onClick={() => navigate(`/project/${projectId}?tab=file_pipeline`)}>
                  Go to Project
                </Button>
                <Button size="large" onClick={() => navigate('/deploy')}>
                  Deploy Another Project
                </Button>
                <Button type="primary" size="large" onClick={() => navigate('/home')}>
                  Back to Dashboard
                </Button>
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box' }}>
      <div style={{ padding: '24px', display: 'flex', gap: '24px', maxWidth: '1400px', width: '100%', margin: '0 auto', flex: 1, minHeight: 0, overflow: 'hidden', boxSizing: 'border-box' }}>
        <div style={{ width: '200px', flexShrink: 0, padding: '24px 0', alignSelf: 'flex-start' }}>
          <h2 style={{ margin: '0 0 24px 0', fontSize: '20px', fontWeight: '600', color: themeStyles.textPrimary, textAlign: 'left' }}>
            Deploy Project
          </h2>
          <Steps
            direction="vertical"
            current={currentStep}
            items={stepItems}
            onChange={setCurrentStep}
            style={{ cursor: 'pointer' }}
          />
        </div>
        <div style={{ background: 'linear-gradient(135deg, rgba(148, 22, 127, 0.1) 0%, rgba(21, 60, 180, 0.1) 100%)', border: '1px solid rgba(148, 22, 127, 0.3)', borderRadius: '8px', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>

        <div style={{ flex: 1, overflowY: 'scroll', overflowX: 'hidden', padding: '24px', minHeight: 0 }}>
          {renderStepContent()}
        </div>
        {currentStep > 0 && currentStep < 5 && (
          <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(148, 22, 127, 0.2)', display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              type="primary" 
              onClick={() => {
                // If on step 1 with a single strategy selected but not yet set, set it now
                if (currentStep === 1 && selectedStrategies.length === 1 && !selectedStrategy) {
                  setSelectedStrategy(selectedStrategies[0]);
                }
                setCurrentStep(currentStep + 1);
              }}
              disabled={
                (currentStep === 1 && !selectedStrategy && selectedStrategies.length !== 1) ||
                (currentStep === 2 && !chunkData) ||
                (currentStep === 3 && !embeddingData) ||
                (currentStep === 4 && !pushData)
              }
            >
              Next
            </Button>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default DeployProject;
